import api from 'lib/api';
import moment from 'moment';

export const GET_GROUP = 'GET_GROUP';

export const GET_GROUPS = 'GET_GROUPS';
export const GET_SCHOOL_GROUPS = 'GET_SCHOOL_GROUPS';
export const GET_MY_GROUPS = 'GET_MY_GROUPS';
export const GET_FILTERED_GROUPS_REQUEST = 'GET_FILTERED_SECTIONS_REQUEST';
export const GET_FILTERED_GROUPS_SUCCESS = 'GET_FILTERED_SECTIONS_SUCCESS';
export const GET_FILTERED_GROUPS_FAILURE = 'GET_FILTERED_SECTIONS_FAILURE';

export const CLEAR_GROUPS = 'CLEAR_GROUPS';
export const CLEAR_GROUP_ID = 'CLEAR_GROUP_ID';
export const CLEAR_GROUP_BY_GRAPHID = 'CLEAR_GROUP_BY_GRAPHID';
export const CLEAR_MY_GROUPS = 'CLEAR_MY_GROUPS';
export const CLEAR_SCHOOL_GROUPS = 'CLEAR_SCHOOL_GROUPS';
export const CLEAR_FILTERED_GROUPS = 'CLEAR_FILTERED_GROUPS';


const fetchSchoolGroups = (schoolId) => ({
	type: GET_SCHOOL_GROUPS,
	payload: new Promise((resolve) => {
		api.get('schools/' + schoolId + '/sections?filter=MENTOR_GROUP;EDUCATION_GROUP').then((response) => {
			resolve(response.data);
		});
	}),
});


const fetchGroupByGraphId = (graphId, expandParams = []) => ({
	type: GET_GROUP,
	payload: new Promise((resolve) => {
		let params = "";

		if (expandParams != null && expandParams.length > 0) {
			params = 'expand=';
			params += expandParams.join(";");
		}

		api.get(`sections/${graphId}?${params}`).then((response) => {
			if (response.status == 204) {
				resolve(null);
				return;
			}

			resolve({ expandParams: expandParams, group: response.data });
		});
	}),
});

const fetchGroupById = (id, expandParams = []) => ({
	type: GET_GROUP,
	payload: new Promise((resolve, reject) => {
		let params = "";

		if (expandParams != null && expandParams.length > 0) {
			params = 'expand=';
			params += expandParams.join(";");
		}

		api.get(`sections/${id}?${params}`)
			.then((response) => {
				var data = response.data;

				if (data && data.courses != null && data.courses.length > 0) {
					data.courses.forEach((element) => {
						if (element.details != null && element.details.length > 0) {
							var activeDetailIndex = element.details.findIndex(
								(element) => element.endDate == null
							);

							if (activeDetailIndex > -1) {
								var activeDetail = element.details[activeDetailIndex];
								element.details.splice(activeDetailIndex, 1);
								element.details.unshift(activeDetail);
							}
						}
					});
				}

				resolve({ expandParams: expandParams, group: response.data });
			})
			.catch((e) => {
				reject(e);
			});
	}),
});

const fetchFilteredGroupsFromApi = (requestPayload) => {
	return async (dispatch) => {
		try {
			const response = await api.post('/sections/search', requestPayload);
			dispatch({
				type: GET_FILTERED_GROUPS_SUCCESS,
				payload: response.data,
			});
		} catch (error) {
			dispatch({
				type: GET_FILTERED_GROUPS_FAILURE,
				error: error.response?.data?.message || error.message,
			});
			throw error;
		}
	};
};

/**
 * Fetches my groups from the api.
 * @returns 
 */
const fetchMyGroups = () => ({
	type: GET_GROUPS,
	payload: new Promise((resolve) => {
		api.get('users/me/sections?type=MENTOR_GROUP;EDUCATION_GROUP&filter=ACTIVE;').then((response) => {
			resolve(response.data);
		});
	}),
})

/**
 * Ensures that the provided group data is up-to-date and contains the required fields.
 * If the group data is outdated (older than 5 minutes) or missing required fields,
 * it dispatches a fetch action to update the group data.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} getState - Function to get the current Redux state.
 * @param {Object} group - The group object to validate.
 * @param {string[]} requiredFields - Array of required field names.
 * @returns {Promise<Object>} - The updated group object.
 */
const ensureGroupIsUpToDate = async (dispatch, getState, group, requiredFields) => {
	const groupLastFetched = moment.utc(group.lastFetched);
	const fiveMinutesAgo = moment.utc().subtract(5, 'minutes');

	// Check if the data is outdated.
	if (groupLastFetched.isBefore(fiveMinutesAgo)) {
		dispatch(clearGroupById(group.id));
		await dispatch(fetchGroupById(group.id, requiredFields));
		group = getState().sectionsV2.groups[group.id];
		return group;
	}

	// Normalize group keys for case-insensitive comparison.
	const normalizedGroup = Object.keys(group).reduce((acc, key) => {
		acc[key.toLowerCase()] = group[key];
		return acc;
	}, {});

	// Check for missing required fields.
	const missingField = requiredFields.some(field => {
		const value = normalizedGroup[field.toLowerCase()];
		if (!value || (Array.isArray(value) && value.length === 0)) {
			return true;
		}
		return false;
	});

	if (missingField) {
		await dispatch(fetchGroupById(group.id, requiredFields));
		group = getState().sectionsV2.groups[group.id];
	}

	return group;
};

/**
 * Filter function to get user's active groups (Education or Mentor groups).
 * 
 * Conditions:
 * - Group type is either "EDUCATION_GROUP" or "MENTOR_GROUP"
 * - Group is not archived
 * - User is (OWNER or MEMBER)
 *
 * @param {Object} group - The group object to check
 * @returns {boolean} - True if group matches criteria
 */
const getMyFilteredActiveGroups = (group) => {
	return (group.type?.toUpperCase() === "EDUCATION_GROUP" || group.type?.toUpperCase() === "MENTOR_GROUP") &&
		!group.archived &&
		group.userRole != null &&
		(group.userRole.toUpperCase() === "OWNER" || group.userRole.toUpperCase() === "MEMBER");
};

/**
 * Filter function to get user's active education groups.
 * 
 * Conditions:
 * - Group type is "EDUCATION_GROUP"
 * - Group is not archived
 * - User is (OWNER or MEMBER)
 *
 * @param {Object} group - The group object to check
 * @returns {boolean} - True if group matches criteria
 */
const getMyFilteredActiveEducationGroups = (group) => {
	return group.type?.toUpperCase() === "EDUCATION_GROUP" &&
		!group.archived &&
		group.userRole != null &&
		(group.userRole.toUpperCase() === "OWNER" || group.userRole.toUpperCase() === "MEMBER");
};

/**
 * Filter function to get user's active mentor groups.
 * 
 * Conditions:
 * - Group type is "MENTOR_GROUP"
 * - Group is not archived
 * - User is (OWNER or MEMBER)
 *
 * @param {Object} group - The group object to check
 * @returns {boolean} - True if group matches criteria
 */
const getMyFilteredActiveMentorGroups = (group) => {
	return group.type?.toUpperCase() === "MENTOR_GROUP" &&
		!group.archived &&
		group.userRole != null &&
		(group.userRole.toUpperCase() === "OWNER" || group.userRole.toUpperCase() === "MEMBER");
};


/**
 * Clear particular group from store, it will also clear from the myGroupsList
 * @param {*} graphId 
 * @returns 
 */
export const clearGroupByGraphId = (graphId) => ({
	type: CLEAR_GROUP_BY_GRAPHID,
	payload: graphId
});


/**
 * Clear particular group from store, it will also clear from the myGroupsList
 * @param {*} groupId 
 * @returns 
 */
export const clearGroupById = (groupId) => ({
	type: CLEAR_GROUP_ID,
	payload: groupId
});

/**
 * Clear my groups from store
 * @returns 
 */
export const clearGroups = () => ({
	type: CLEAR_GROUPS,
	payload: null
});

/**
 * Clear school groups from store
 * @returns
 */
export const clearSchoolGroups = () => ({
	type: CLEAR_SCHOOL_GROUPS,
	payload: null
});

/**
 * Clear filtered groups from store
 * @returns
 */
export const clearFilteredGroups = () => ({
	type: CLEAR_FILTERED_GROUPS,
	payload: null
});

/**
 * Get all of my groups including mentor and education groups
 * @returns List of my groups including mentor and education groups
 */
export const getMyGroups = () => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = Object.keys(state.sectionsV2.groups).filter(getMyFilteredActiveGroups).sort((a, b) => a.title.localeCompare(b.title));

		if (Object.keys(groups).length === 0) {
			await dispatch(fetchMyGroups());
			state = getState();
			groups = Object.values(state.sectionsV2.groups);
			groups = groups.filter(getMyFilteredActiveGroups).sort((a, b) => a.title.localeCompare(b.title));
			return groups;
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedGroups = state.sectionsV2.lastFetchedGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');

		// Check if the groups are outdated and need to be fetched again
		let isOutdatedGroups = lastFetchedGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(clearGroups());
			await dispatch(fetchMyGroups());
			state = getState();
			groups = Object.values(state.sectionsV2.groups);
			groups = groups.filter(getMyFilteredActiveGroups).sort((a, b) => a.title.localeCompare(b.title));
			return groups;
		}

		return groups;
	}
};

/***
 * Get all of my owned mentor groups
 */
export const getMyOwnedMentorGroups = () => {
	return async (dispatch, getState) => {
		var mentorGroups = await dispatch(getMyMentorGroups());
		return mentorGroups.filter(group => group.userRole.toUpperCase() === 'OWNER');
	};
};

/**
 * Get all of my owned education groups
 */
export const getMyOwnedEducationGroups = () => {
	return async (dispatch, getState) => {
		var educationGroups = await dispatch(getMyEducationGroups());
		return educationGroups.filter(group => group.userRole.toUpperCase() === 'OWNER');
	};
};


/**
 * Get all of my mentor groups
 * @returns List of my mentor groups
 */
export const getMyMentorGroups = () => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = state.sectionsV2.groups;
		let mentorGroups = Object.values(groups).filter(getMyFilteredActiveMentorGroups).sort((a, b) => a.title.localeCompare(b.title));

		if (mentorGroups.length === 0) {
			await dispatch(fetchMyGroups());
			state = getState();
			groups = state.sectionsV2.groups;
			mentorGroups = Object.values(groups).filter(getMyFilteredActiveMentorGroups).sort((a, b) => a.title.localeCompare(b.title));
			return mentorGroups;
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedGroups = state.sectionsV2.lastFetchedGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');

		// Check if the groups are outdated and need to be fetched again
		let isOutdatedGroups = lastFetchedGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(clearGroups());
			await dispatch(fetchMyGroups());
			state = getState();
			groups = state.sectionsV2.groups;
			mentorGroups = Object.values(groups).filter(getMyFilteredActiveMentorGroups).sort((a, b) => a.title.localeCompare(b.title));;
			return mentorGroups;
		}

		return mentorGroups;
	};
};

/**
 * Get all of my education groups
 * @returns List of my education groups
 */
export const getMyEducationGroups = () => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = state.sectionsV2.groups;
		let educationGroups = Object.values(groups).filter(getMyFilteredActiveEducationGroups).sort((a, b) => a.title.localeCompare(b.title));

		if (educationGroups.length === 0 || state.sectionsV2.lastFetchedGroups == null) {
			await dispatch(fetchMyGroups());
			state = getState();
			groups = state.sectionsV2.groups;
			educationGroups = Object.values(groups).filter(getMyFilteredActiveEducationGroups).sort((a, b) => a.title.localeCompare(b.title));
			return educationGroups;
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedGroups = state.sectionsV2.lastFetchedGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');

		// Check if the groups are outdated and need to be fetched again
		let isOutdatedGroups = lastFetchedGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(clearGroups());
			await dispatch(fetchMyGroups());
			state = getState();
			groups = state.sectionsV2.groups;
			educationGroups = Object.values(groups).filter(getMyFilteredActiveEducationGroups).sort((a, b) => a.title.localeCompare(b.title));
			return educationGroups;
		}

		return educationGroups;
	}
};

/**
 * Retrieves a group by its unique ID from the Redux store or fetches it from the server if needed.
 *
 * @param {string|number} id - The unique identifier of the group.
 * @param {string[]} [requiredFields=[]] - Array of required field names.
 * @returns {Object} - A Group object.
 */
export const getGroupById = (id, requiredFields = []) => async (dispatch, getState) => {
	let state = getState();
	let group = state.sectionsV2.groups[id];

	if (!group) {
		console.log("Group not found in store, fetching from server by ID.");
		if (id !== 0 && id !== '0') {
			await dispatch(fetchGroupById(id, requiredFields));
		}
		state = getState();
		group = state.sectionsV2.groups[id];
		return group;
	}

	group = await ensureGroupIsUpToDate(dispatch, getState, group, requiredFields);
	return group;
};

/**
 * Retrieves a group by its graph ID from the Redux store or fetches it from the server if needed.
 * @param {string} graphId - The graph identifier of the group.
 * @param {string[]} [requiredFields=[]] - Array of required field names.
 * @returns {Object} - A Group object.
 */
export const getGroupByGraphId = (graphId, requiredFields = []) => async (dispatch, getState) => {
	let state = getState();

	// groups is stored as an object keyed by ID.
	// Use Object.values to search by graphId.
	const groups = Object.values(state.sectionsV2.groups);
	let group = groups.find(g => g.graphId === graphId);

	if (!group) {
		console.log("Group not found in store, fetching from server by Graph ID.");
		await dispatch(fetchGroupByGraphId(graphId, requiredFields));
		state = getState();
		const updatedGroups = Object.values(state.sectionsV2.groups);
		group = updatedGroups.find(g => g.graphId === graphId);
		return group;
	}

	group = await ensureGroupIsUpToDate(dispatch, getState, group, requiredFields);
	return group;
};

/**
 * get all education and mentor groups for a particular school 
 * @param {*} schoolId 
 * @returns 
 */
export const getSchoolGroups = (schoolId) => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = state.sectionsV2.groups;

		if (Object.keys(groups).length === 0 || state.sectionsV2.lastFetchedSchoolGroups == null) {
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			return Object.values(groups);
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedSchoolGroups = state.sectionsV2.lastFetchedSchoolGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');
		let isOutdatedGroups = lastFetchedSchoolGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(clearSchoolGroups());
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			return Object.values(groups);
		}

		return Object.values(groups);
	}
};

/**
 * get all education groups for a particular school 
 * @param {*} schoolId 
 * @returns 
 */
export const getSchoolEducationGroups = (schoolId) => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = state.sectionsV2.groups;
		let educationGroups = Object.values(groups).filter((group) => group.type != null && group.type.toUpperCase() === "EDUCATION_GROUP");

		if (educationGroups.length === 0 || state.sectionsV2.lastFetchedSchoolGroups == null) {
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			educationGroups = Object.values(groups).filter((group) => group.type != null && group.type.toUpperCase() === "EDUCATION_GROUP");
			return educationGroups;
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedSchoolGroups = state.sectionsV2.lastFetchedSchoolGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');
		let isOutdatedGroups = lastFetchedSchoolGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			educationGroups = Object.values(groups).filter((group) => group.type != null && group.type.toUpperCase() === "EDUCATION_GROUP");
			return educationGroups;
		}

		return educationGroups;
	}
};


/**
 * Get all mentor groups for a particular school
 * @param {*} schoolId 
 * @returns 
 */
export const getSchoolMentorGroups = (schoolId) => {
	return async (dispatch, getState) => {
		let state = getState();
		let groups = state.sectionsV2.groups;

		let mentorGroups = Object.values(groups).filter((group) => group.type.toUpperCase() === "MENTOR_GROUP");

		if (mentorGroups.length === 0 || state.sectionsV2.lastFetchedSchoolGroups == null) {
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			mentorGroups = Object.values(groups).filter((group) => group.type.toUpperCase() === "MENTOR_GROUP");
			return mentorGroups;
		}

		/**
		 * Check when the groups was fetched from the server, since we
		 */
		let lastFetchedSchoolGroups = state.sectionsV2.lastFetchedSchoolGroups;
		let fiveMinutesAgo = moment.utc().subtract(5, 'minutes');
		let isOutdatedGroups = lastFetchedSchoolGroups.isBefore(fiveMinutesAgo);

		if (isOutdatedGroups) {
			await dispatch(fetchSchoolGroups(schoolId));
			state = getState();
			groups = state.sectionsV2.groups;
			mentorGroups = Object.values(groups).filter((group) => group.type.toUpperCase() === "MENTOR_GROUP");
			return mentorGroups;
		}

		return mentorGroups;
	}
};

/**
* Fetches filtered groups for a particular school based on the provided criteria.
*
* @param {Object} requestPayload - The filter and pagination criteria.
* @param {string[]} [requestPayload.userIds] - Optional array of user IDs; only groups where these users are members will be returned.
* @param {string} [requestPayload.query] - Optional search term to filter groups by name or other searchable fields.
* @param {string} [requestPayload.filter] - Optional semicolon-separated list of group types (e.g., "MENTOR_GROUP;EDUCATION_GROUP").
* @param {string} [requestPayload.sortBy] - Optional field name to sort results by (e.g., "title").
* @param {number} [requestPayload.pageIndex] - Optional 0-based index for pagination.
* @param {number} [requestPayload.pageSize] - Optional number of results per page.
*
* @returns {Function} Redux Thunk action that dispatches filtered section results or an error.
*/

export const getFilteredGroups = (requestPayload) => {
	return async (dispatch, getState) => {
		dispatch({ type: GET_FILTERED_GROUPS_REQUEST });

		try {
			await dispatch(fetchFilteredGroupsFromApi(requestPayload));

			const state = getState();
			const groups = state.sectionsV2.filteredGroups;
			return groups;
		} catch (error) {
			console.error('Failed to fetch filtered groups', error);
			return [];
		}
	};
};
